import React, { useState, useEffect, useRef } from "react";
import {
	Play,
	RotateCcw,
	Send,
	X,
	Bot,
	User,
	Check,
	CheckCheck,
	Clock,
	Tag,
	Users,
	Layers,
	FastForward,
	Code,
	Image as ImageIcon,
	FileText,
	Mic,
	Video,
	Sparkles,
	CornerUpLeft,
	ArrowRight,
	Info,
	AlertCircle,
	Smartphone,
	Braces,
	CheckCircle2,
	MessageSquare,
	Zap,
	Timer,
} from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";
import { cn } from "../../lib/utils";
import api from "../../services/api";

const interpolar = (texto, context) => {
	if (!texto) return "";
	return String(texto).replace(/\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g, (match, key) => {
		const val = key.split(".").reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), context);
		return val !== undefined ? String(val) : match;
	});
};

const VALIDADORES = {
	texto: () => true,
	numero: (val) => /^-?\d+([.,]\d+)?$/.test(String(val || "").trim()),
	email: (val) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(val || "").trim()),
	telefone: (val) => String(val || "").replace(/\D/g, "").length >= 10,
	data: (val) => /^\d{2}\/\d{2}\/\d{4}$/.test(String(val || "").trim()),
};

const AVISOS_VALIDACAO = {
	numero: "Por favor, digite um número válido.",
	email: "Esse e-mail não parece válido. Por favor, confira e tente novamente.",
	telefone: "Por favor, informe um telefone com DDD válido.",
	data: "Por favor, informe uma data no formato DD/MM/AAAA.",
};

const FlowSimulator = ({ open, onClose, flowName, nodes = [], edges = [] }) => {
	const [messages, setMessages] = useState([]);
	const [context, setContext] = useState({});
	const [userInput, setUserInput] = useState("");
	const [isTyping, setIsTyping] = useState(false);
	const [typingText, setTypingText] = useState("Digitando...");
	const [waitingNode, setWaitingNode] = useState(null);
	const [fastMode, setFastMode] = useState(false);
	const [showVariables, setShowVariables] = useState(false);
	const [isFinished, setIsFinished] = useState(false);

	const chatEndRef = useRef(null);
	const inputRef = useRef(null);
	const isExecutingRef = useRef(false);

	const scrollToBottom = () => {
		chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
	};

	useEffect(() => {
		scrollToBottom();
	}, [messages, isTyping]);

	const findNextNode = (currentId, handle) => {
		let edge = edges.find((e) => {
			if (e.source !== currentId) return false;
			if (handle === undefined) return true;
			return (e.sourceHandle || "") === handle;
		});

		if (!edge && handle) {
			edge = edges.find((e) => e.source === currentId);
		}

		if (!edge) return null;
		return nodes.find((n) => n.id === edge.target) || null;
	};

	const addMessage = (msg) => {
		const newMsg = {
			id: `msg-${Date.now()}-${Math.random()}`,
			timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
			...msg,
		};
		setMessages((prev) => [...prev, newMsg]);
		return newMsg;
	};

	const delay = (ms) => new Promise((resolve) => setTimeout(resolve, fastMode ? Math.min(ms, 150) : ms));

	const runFromNode = async (startNode, initialContext, userResponse = null) => {
		if (isExecutingRef.current) return;
		isExecutingRef.current = true;

		let currentContext = { ...initialContext };
		let current = startNode;
		let steps = 0;
		const maxSteps = 50;

		setWaitingNode(null);
		setIsFinished(false);

		// Retomada de nó que estava aguardando resposta
		if (userResponse && current) {
			const data = current.data || {};

			if (current.type === "ai") {
				const opcoes = data.options || [];
				const rawResp = String(userResponse).trim().toLowerCase();

				// Verifica se a resposta bate com alguma opção direta do nó de IA
				const opcaoEncontrada = opcoes.find(
					(o) =>
						String(o.number).trim().toLowerCase() === rawResp ||
						String(o.label).trim().toLowerCase() === rawResp ||
						(rawResp.length >= 2 && String(o.label).trim().toLowerCase().includes(rawResp))
				);

				if (opcaoEncontrada) {
					currentContext.ai_option = {
						number: opcaoEncontrada.number,
						label: opcaoEncontrada.label,
					};
					currentContext.forma_pagamento = opcaoEncontrada.label;
					setContext({ ...currentContext });

					addMessage({
						type: "system",
						text: `Opção selecionada: "${opcaoEncontrada.number} - ${opcaoEncontrada.label}"`,
						icon: CheckCircle2,
					});

					current = findNextNode(current.id, `opt-${opcaoEncontrada.number}`) || findNextNode(current.id);
				} else {
					// Consulta a IA real através do endpoint do backend
					setIsTyping(true);
					setTypingText("IA pensando...");

					const history = Array.isArray(currentContext._aiHistory) ? [...currentContext._aiHistory] : [];
					history.push({ role: "user", content: userResponse });

					try {
						const res = await api.post("/flows/simulate-ai", {
							prompt: data.prompt,
							messages: history,
							options: opcoes,
						});

						setIsTyping(false);
						const aiData = res.data || {};
						const replyMsg = aiData.message || "Entendido!";

						// Extrai variáveis retornadas pela IA
						if (aiData.variables && typeof aiData.variables === "object") {
							for (const [k, v] of Object.entries(aiData.variables)) {
								if (v !== undefined && v !== null && String(v).trim() !== "" && String(v).trim() !== "null") {
									currentContext[k] = v;
								}
							}
						}

						history.push({ role: "assistant", content: replyMsg });
						currentContext._aiHistory = history;
						setContext({ ...currentContext });

						const isSelectAction = aiData.action === "select";
						const chosenOpt = aiData.option
							? opcoes.find(
									(o) =>
										String(o.number) === String(aiData.option) ||
										String(o.label).toLowerCase() === String(aiData.option).toLowerCase()
							  )
							: null;

						const showButtonsNow =
							opcoes.length > 0 &&
							(isSelectAction || /pagamento|pagar|financiamento|consórcio|à vista|parcelado/i.test(replyMsg));

						addMessage({
							type: "bot",
							text: replyMsg,
							isAi: true,
							options: showButtonsNow ? opcoes : undefined,
							interactiveType: showButtonsNow ? "buttons" : undefined,
							nodeId: current.id,
						});

						if (isSelectAction && chosenOpt) {
							currentContext.ai_option = {
								number: chosenOpt.number,
								label: chosenOpt.label,
							};
							currentContext.forma_pagamento = chosenOpt.label;
							setContext({ ...currentContext });
							current = findNextNode(current.id, `opt-${chosenOpt.number}`) || findNextNode(current.id);
						} else {
							// Continua na conversa do nó de IA
							setWaitingNode(current);
							isExecutingRef.current = false;
							return;
						}
					} catch (err) {
						setIsTyping(false);
						addMessage({
							type: "bot",
							text: "Pode me dar mais detalhes sobre o que precisa?",
							isAi: true,
						});
						setWaitingNode(current);
						isExecutingRef.current = false;
						return;
					}
				}
			} else if (current.type === "question") {
				const varName = data.variable || "resposta";
				currentContext[varName] = userResponse;
				currentContext.lastAnswer = userResponse;
				setContext({ ...currentContext });

				addMessage({
					type: "system",
					text: `Variável salva: {{${varName}}} = "${userResponse}"`,
					icon: Braces,
				});

				current = findNextNode(current.id);
			} else if (current.type === "menu" || current.type === "buttons" || current.type === "list") {
				const opcoes = data.options || [];
				const escolha = String(userResponse).trim();
				const opcao = opcoes.find(
					(o) => String(o.number).trim() === escolha || String(o.label).trim().toLowerCase() === escolha.toLowerCase()
				);

				if (opcao) {
					currentContext.menu = { number: opcao.number, label: opcao.label };
					if (data.variable) {
						currentContext[data.variable] = opcao.label;
					}
					setContext({ ...currentContext });
					current = findNextNode(current.id, `opt-${opcao.number}`);
				} else {
					currentContext._menuTentativas = (currentContext._menuTentativas || 0) + 1;
					const maxAttempts = Number(data.maxAttempts) || 3;

					if (currentContext._menuTentativas >= maxAttempts) {
						addMessage({
							type: "system",
							text: `Limite de ${maxAttempts} tentativas atingido. Seguindo saída padrão.`,
							icon: Info,
						});
						delete currentContext._menuTentativas;
						current = findNextNode(current.id, "fallback");
					} else {
						setIsTyping(true);
						await delay(600);
						setIsTyping(false);

						const invMsg = interpolar(
							data.invalidMessage || "Opção inválida. Por favor, tente novamente.",
							currentContext
						);
						addMessage({
							type: "bot",
							text: invMsg,
						});

						setWaitingNode(current);
						isExecutingRef.current = false;
						return;
					}
				}
			} else if (current.type === "followUp") {
				const isNoReply =
					userResponse === "2" ||
					userResponse === "__TIMEOUT__" ||
					String(userResponse).toLowerCase().includes("não respondeu") ||
					String(userResponse).toLowerCase().includes("disparar etapas");

				if (isNoReply) {
					const steps = data.steps || [];
					for (let i = 0; i < steps.length; i++) {
						const step = steps[i];
						const delayText = `${step.delay || 1} ${step.unit || "horas"}`;
						addMessage({
							type: "system",
							text: `[Follow-up] Tempo esgotado (${delayText}). Enviando etapa ${i + 1}...`,
							icon: Clock,
						});
						const stepMsg = interpolar(step.message || "", currentContext);
						if (stepMsg) {
							setIsTyping(true);
							await delay(500);
							setIsTyping(false);
							addMessage({
								type: "bot",
								text: stepMsg,
							});
						}
					}
					if (data.closeOnComplete) {
						addMessage({
							type: "system",
							text: "Atendimento encerrado automaticamente após conclusão do Follow-up.",
							icon: CheckCircle2,
						});
					}
					current = findNextNode(current.id, "completed") || findNextNode(current.id);
				} else {
					addMessage({
						type: "system",
						text: "Cliente respondeu durante o Follow-up → Seguindo saída 'Cliente respondeu'",
						icon: CheckCircle2,
					});
					current = findNextNode(current.id, "responded") || findNextNode(current.id);
				}
			}
		}

		while (current && steps < maxSteps) {
			steps += 1;
			const node = current;
			const data = node.data || {};

			switch (node.type) {
				case "trigger": {
					addMessage({
						type: "system",
						text: `Gatilho acionado (${data.triggerType || "manual"})`,
						icon: Zap,
					});
					current = findNextNode(node.id);
					break;
				}

				case "ai": {
					setIsTyping(true);
					setTypingText("IA pensando...");
					await delay(800);
					setIsTyping(false);

					const saudacao = interpolar(
						data.greeting || "Olá! Como posso te ajudar hoje?",
						currentContext
					);

					currentContext._aiHistory = [{ role: "assistant", content: saudacao }];
					setContext({ ...currentContext });

					addMessage({
						type: "bot",
						text: saudacao,
						isAi: true,
					});

					setWaitingNode(node);
					isExecutingRef.current = false;
					return;
				}

				case "sendMessage":
				case "content": {
					const items = data.items || [];
					if (items.length > 0) {
						for (const item of items) {
							if (item.kind === "text" && item.value) {
								const text = interpolar(item.value, currentContext);
								setIsTyping(true);
								setTypingText("Digitando...");
								await delay(Math.min(Math.max(text.length * 15, 600), 1800));
								setIsTyping(false);
								addMessage({
									type: "bot",
									text,
								});
							} else if (item.kind === "media" || item.kind === "image" || item.kind === "audio" || item.kind === "video") {
								setIsTyping(true);
								setTypingText(item.kind === "audio" ? "Gravando áudio..." : "Enviando mídia...");
								await delay(1000);
								setIsTyping(false);
								addMessage({
									type: "bot",
									media: {
										type: item.kind,
										url: item.url || item.value,
										caption: interpolar(item.caption || "", currentContext),
									},
								});
							} else if (item.kind === "delay") {
								const secs = Number(item.seconds) || 2;
								setIsTyping(true);
								setTypingText("Digitando...");
								await delay(secs * 1000);
								setIsTyping(false);
							}
						}
					} else if (data.body) {
						const body = interpolar(data.body, currentContext);
						if (body.trim()) {
							setIsTyping(true);
							setTypingText("Digitando...");
							await delay(Math.min(Math.max(body.length * 15, 600), 1800));
							setIsTyping(false);
							addMessage({
								type: "bot",
								text: body,
							});
						}
					}
					current = findNextNode(node.id);
					break;
				}

				case "media": {
					setIsTyping(true);
					setTypingText(data.mediaType === "audio" ? "Gravando áudio..." : "Enviando mídia...");
					await delay(1200);
					setIsTyping(false);

					addMessage({
						type: "bot",
						media: {
							type: data.mediaType || "image",
							url: data.url,
							caption: interpolar(data.caption || "", currentContext),
						},
					});
					current = findNextNode(node.id);
					break;
				}

				case "menu":
				case "buttons":
				case "list": {
					const msg = interpolar(data.message || "", currentContext);
					const footer = interpolar(data.footer || "", currentContext);
					const options = data.options || [];

					setIsTyping(true);
					setTypingText("Digitando...");
					await delay(800);
					setIsTyping(false);

					addMessage({
						type: "bot",
						text: msg,
						footer: footer,
						options: options,
						interactiveType: node.type,
						nodeId: node.id,
					});

					setWaitingNode(node);
					isExecutingRef.current = false;
					return;
				}

				case "question": {
					const msg = interpolar(data.message || "", currentContext);
					setIsTyping(true);
					setTypingText("Digitando...");
					await delay(800);
					setIsTyping(false);

					addMessage({
						type: "bot",
						text: msg,
						isQuestion: true,
						answerType: data.answerType || "texto",
					});

					setWaitingNode(node);
					isExecutingRef.current = false;
					return;
				}

				case "delay": {
					const secs = Number(data.seconds) || 2;
					addMessage({
						type: "system",
						text: `Aguardando atraso de ${secs}s...`,
						icon: Clock,
					});
					setIsTyping(true);
					setTypingText("Digitando...");
					await delay(secs * 1000);
					setIsTyping(false);
					current = findNextNode(node.id);
					break;
				}

				case "followUp": {
					const steps = data.steps || [];
					const count = steps.length;
					const opcoesSimulacao = [
						{ number: "1", label: "Simular: Cliente respondeu" },
						{ number: "2", label: "Simular: Cliente NÃO respondeu (disparar etapas e avançar)" },
					];

					addMessage({
						type: "bot",
						text: `⏳ *Follow-up Ativado* (${count} etapa${count !== 1 ? "s" : ""})\nO robô aguarda o tempo configurado caso o cliente não responda. Escolha abaixo como deseja simular:`,
						options: opcoesSimulacao,
						interactiveType: "buttons",
						nodeId: node.id,
					});

					setWaitingNode(node);
					isExecutingRef.current = false;
					return;
				}

				case "condition": {
					const actual = (data.field || "")
						.split(".")
						.reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), currentContext);
					const expected = data.value;
					let matches = false;

					if (data.operator === "exists") {
						matches = actual !== undefined && actual !== null && actual !== "";
					} else if (data.operator === "contains") {
						matches = String(actual ?? "").toLowerCase().includes(String(expected ?? "").toLowerCase());
					} else {
						matches = String(actual ?? "") === String(expected ?? "");
					}

					addMessage({
						type: "system",
						text: `Condição: "${data.field || ""}" ${data.operator || "="} "${data.value || ""}" → ${
							matches ? "Sim (Verdadeiro)" : "Não (Falso)"
						}`,
						icon: Info,
					});

					current = findNextNode(node.id, matches ? "true" : "false");
					break;
				}

				case "setVariable": {
					const varName = data.variable || "var";
					const val = interpolar(data.value || "", currentContext);
					currentContext[varName] = val;
					setContext({ ...currentContext });

					addMessage({
						type: "system",
						text: `Variável definida: {{${varName}}} = "${val}"`,
						icon: Braces,
					});

					current = findNextNode(node.id);
					break;
				}

				case "jump": {
					const targetId = data.targetNodeId;
					const targetNode = nodes.find((n) => n.id === targetId);
					addMessage({
						type: "system",
						text: `Pular para: "${data.targetLabel || targetId || "próximo bloco"}"`,
						icon: CornerUpLeft,
					});
					current = targetNode || null;
					break;
				}

				case "randomizer": {
					const percent = Number(data.percent) || 50;
					const roll = Math.floor(Math.random() * 100) + 1;
					const isA = roll <= percent;
					addMessage({
						type: "system",
						text: `Divisor de fluxo: ${roll}% (Caminho ${isA ? "A" : "B"})`,
						icon: Sparkles,
					});
					current = findNextNode(node.id, isA ? "handle-a" : "handle-b");
					break;
				}

				case "addTag": {
					addMessage({
						type: "system",
						text: `Etiqueta aplicada: "${data.tagName || data.tagId || "Etiqueta"}"`,
						icon: Tag,
					});
					current = findNextNode(node.id);
					break;
				}

				case "transferQueue": {
					addMessage({
						type: "system",
						text: `Transferido para a fila: "${data.queueName || data.queueId || "Fila de Atendimento"}"`,
						icon: Users,
					});
					current = findNextNode(node.id);
					break;
				}

				case "assignUser": {
					addMessage({
						type: "system",
						text: `Atribuído ao atendente: "${data.userName || data.userId || "Atendente"}"`,
						icon: User,
					});
					current = findNextNode(node.id);
					break;
				}

				case "ticketStatus": {
					const labels = {
						open: "Aberto",
						pending: "Pendente (Aguardando)",
						closed: "Resolvido/Fechado",
					};
					addMessage({
						type: "system",
						text: `Status do atendimento alterado para: "${labels[data.status] || data.status}"`,
						icon: CheckCircle2,
					});
					current = findNextNode(node.id);
					break;
				}

				case "note": {
					addMessage({
						type: "internalNote",
						text: interpolar(data.note || "", currentContext),
					});
					current = findNextNode(node.id);
					break;
				}

				case "integration":
				case "httpRequest": {
					addMessage({
						type: "system",
						text: `Integração executada (${data.service || data.method || "HTTP"} ${data.url || ""})`,
						icon: Layers,
					});
					current = findNextNode(node.id);
					break;
				}

				case "typing": {
					const secs = Math.min(Math.max(Number(data.seconds) || 3, 1), 300);
					setIsTyping(true);
					setTypingText("Digitando...");
					await delay(Math.min(secs * 1000, 3000));
					setIsTyping(false);
					addMessage({
						type: "system",
						text: `Robô simulou "Digitando" (${secs}s)`,
						icon: Clock,
					});
					current = findNextNode(node.id);
					break;
				}

				case "markRead": {
					addMessage({
						type: "system",
						text: `Conversa marcada como lida (azul/visto)`,
						icon: CheckCircle2,
					});
					current = findNextNode(node.id);
					break;
				}

				case "archive": {
					addMessage({
						type: "system",
						text: data.desfazer ? "Conversa desarquivada" : "Conversa arquivada",
						icon: Layers,
					});
					current = findNextNode(node.id);
					break;
				}

				case "block": {
					addMessage({
						type: "system",
						text: data.desfazer ? "Contato desbloqueado" : "Contato bloqueado",
						icon: Info,
					});
					current = findNextNode(node.id);
					break;
				}

				case "board": {
					addMessage({
						type: "system",
						text: `Atendimento movido no quadro para a raia: "${data.laneName || data.laneId || "Raia"}"`,
						icon: Layers,
					});
					current = findNextNode(node.id);
					break;
				}

				case "createDeal": {
					addMessage({
						type: "system",
						text: `Oportunidade criada no Funil CRM: "${interpolar(data.title || "Nova Oportunidade", currentContext)}"`,
						icon: Layers,
					});
					current = findNextNode(node.id);
					break;
				}

				case "end": {
					if (data.message && data.message.trim()) {
						const finalMsg = interpolar(data.message, currentContext);
						setIsTyping(true);
						await delay(600);
						setIsTyping(false);
						addMessage({
							type: "bot",
							text: finalMsg,
						});
					}
					addMessage({
						type: "system",
						text: `Fluxo finalizado${data.closeTicket ? " (Atendimento encerrado)" : ""}`,
						icon: CheckCircle2,
						isEnd: true,
					});
					setIsFinished(true);
					current = null;
					break;
				}

				default: {
					current = findNextNode(node.id);
					break;
				}
			}
		}

		if (!current && !waitingNode) {
			setIsFinished(true);
		}

		isExecutingRef.current = false;
	};

	const startSimulation = () => {
		setMessages([]);
		const initialCtx = {};
		setContext(initialCtx);
		setUserInput("");
		setIsFinished(false);

		const triggerNode = nodes.find((n) => n.type === "trigger") || nodes[0];
		if (triggerNode) {
			runFromNode(triggerNode, initialCtx);
		} else {
			addMessage({
				type: "system",
				text: "Nenhum bloco de gatilho encontrado no fluxo.",
				icon: AlertCircle,
			});
		}
	};

	useEffect(() => {
		if (open) {
			startSimulation();
		}
	}, [open]);

	const handleOptionSelect = (option) => {
		if (isTyping || !waitingNode) return;

		addMessage({
			type: "user",
			text: `${option.number} - ${option.label}`,
		});

		runFromNode(waitingNode, context, String(option.number));
	};

	const handleSendMessage = (e) => {
		e?.preventDefault();
		if (!userInput.trim() || isTyping) return;

		const text = userInput.trim();
		setUserInput("");

		addMessage({
			type: "user",
			text: text,
		});

		if (waitingNode) {
			const data = waitingNode.data || {};

			if (waitingNode.type === "question") {
				const ansType = data.answerType || "texto";
				const validator = VALIDADORES[ansType] || VALIDADORES.texto;

				if (!validator(text)) {
					setIsTyping(true);
					setTimeout(() => {
						setIsTyping(false);
						const customWarning = data.invalidMessage ? interpolar(data.invalidMessage, context) : null;
						addMessage({
							type: "bot",
							text: customWarning || AVISOS_VALIDACAO[ansType] || "Formato de resposta inválido.",
						});
					}, 400);
					return;
				}
			}

			runFromNode(waitingNode, context, text);
		} else {
			setTimeout(() => {
				addMessage({
					type: "system",
					text: "O fluxo já foi concluído. Clique em Reiniciar para testar novamente do início.",
					icon: Info,
				});
			}, 300);
		}
	};

	return (
		<Dialog open={open} onOpenChange={(v) => !v && onClose()}>
			<DialogContent className="flex h-[90vh] max-h-[760px] w-full max-w-4xl flex-col p-0 overflow-hidden sm:rounded-2xl border bg-background shadow-2xl [&>button.absolute]:hidden">
				{/* Top bar */}
				<DialogHeader className="flex flex-row items-center justify-between border-b px-4 py-3 bg-card/80 backdrop-blur shrink-0 space-y-0">
					<div className="flex items-center gap-3">
						<div className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400">
							<Bot className="h-5 w-5" />
							<span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-background bg-emerald-500" />
						</div>
						<div>
							<DialogTitle className="text-sm font-semibold flex items-center gap-2">
								<span>{flowName || "Assistente Virtual"}</span>
								<Badge variant="outline" className="text-[10px] uppercase font-mono px-1.5 py-0">
									Simulador
								</Badge>
							</DialogTitle>
							<p className="text-xs text-muted-foreground">
								{isTyping ? typingText : waitingNode ? "Aguardando sua resposta..." : "Simulação ativa (sem salvar)"}
							</p>
						</div>
					</div>

					<div className="flex items-center gap-1.5">
						<TooltipProvider>
							<Tooltip>
								<TooltipTrigger asChild>
									<Button
										variant="ghost"
										size="sm"
										onClick={() => setFastMode((v) => !v)}
										className={cn("h-8 gap-1 px-2 text-xs", fastMode && "text-amber-500 font-semibold bg-amber-500/10")}
									>
										<FastForward className="h-3.5 w-3.5" />
										<span className="hidden sm:inline">{fastMode ? "Rápido" : "Normal"}</span>
									</Button>
								</TooltipTrigger>
								<TooltipContent side="bottom" sideOffset={6}>
									{fastMode ? "Atrasos acelerados (modo rápido)" : "Atrasos normais e realistas"}
								</TooltipContent>
							</Tooltip>

							<Tooltip>
								<TooltipTrigger asChild>
									<Button
										variant={showVariables ? "secondary" : "ghost"}
										size="sm"
										onClick={() => setShowVariables((v) => !v)}
										className="h-8 gap-1 px-2 text-xs"
									>
										<Braces className="h-3.5 w-3.5 text-primary" />
										<span className="hidden sm:inline">Variáveis</span>
									</Button>
								</TooltipTrigger>
								<TooltipContent side="bottom" sideOffset={6}>Inspecionar variáveis do fluxo</TooltipContent>
							</Tooltip>

							<Tooltip>
								<TooltipTrigger asChild>
									<Button variant="ghost" size="icon" onClick={startSimulation} className="h-8 w-8 text-muted-foreground hover:text-foreground">
										<RotateCcw className="h-4 w-4" />
									</Button>
								</TooltipTrigger>
								<TooltipContent side="bottom" sideOffset={6}>Reiniciar teste do início</TooltipContent>
							</Tooltip>

							<Tooltip>
								<TooltipTrigger asChild>
									<Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8 text-muted-foreground hover:text-foreground">
										<X className="h-4 w-4" />
									</Button>
								</TooltipTrigger>
								<TooltipContent side="bottom" sideOffset={6}>Fechar simulador</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					</div>
				</DialogHeader>

				{/* Main area with Chat and Variables Inspector */}
				<div className="flex flex-1 min-h-0 overflow-hidden">
					{/* Chat Screen (Clean WhatsApp/Typebot Style) */}
					<div className="flex flex-1 flex-col min-w-0 bg-[#efeae2]/40 dark:bg-muted/20 relative">
						{/* Messages list */}
						<div className="flex-1 overflow-y-auto p-4 space-y-3 relative z-10">
							{messages.map((msg) => {
								if (msg.type === "system") {
									const IconComp = msg.icon || Info;
									return (
										<div key={msg.id} className="flex justify-center my-1.5">
											<div className={cn(
												"flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-medium shadow-sm",
												msg.isEnd
													? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-300/40"
													: "bg-muted/80 text-muted-foreground border backdrop-blur-xs"
											)}>
												<IconComp className="h-3 w-3 shrink-0" />
												<span>{msg.text}</span>
											</div>
										</div>
									);
								}

								if (msg.type === "internalNote") {
									return (
										<div key={msg.id} className="flex justify-center my-1.5">
											<div className="max-w-md rounded-xl bg-amber-500/10 border border-amber-500/30 p-2.5 text-xs text-amber-900 dark:text-amber-200 shadow-sm">
												<div className="flex items-center gap-1 font-semibold mb-0.5 text-amber-700 dark:text-amber-400">
													<FileText className="h-3 w-3" />
													Nota interna gravada no Ticket:
												</div>
												<p className="whitespace-pre-wrap">{msg.text}</p>
											</div>
										</div>
									);
								}

								if (msg.type === "user") {
									return (
										<div key={msg.id} className="flex justify-end animate-in fade-in slide-in-from-bottom-2 duration-150">
											<div className="max-w-[80%] rounded-2xl rounded-tr-xs bg-emerald-600 text-white px-3.5 py-2 text-sm shadow-md">
												<p className="whitespace-pre-wrap leading-relaxed">{msg.text}</p>
												<div className="mt-1 flex items-center justify-end gap-1 text-[10px] text-emerald-100/80">
													<span>{msg.timestamp}</span>
													<CheckCheck className="h-3 w-3" />
												</div>
											</div>
										</div>
									);
								}

								// Bot message
								return (
									<div key={msg.id} className="flex justify-start animate-in fade-in slide-in-from-bottom-2 duration-150">
										<div className="max-w-[82%] rounded-2xl rounded-tl-xs bg-card border border-border/80 px-3.5 py-2.5 text-sm shadow-md text-foreground">
											{msg.isAi && (
												<div className="mb-1 flex items-center gap-1 text-[11px] font-semibold text-primary">
													<Sparkles className="h-3 w-3" />
													Inteligência Artificial (IA)
												</div>
											)}

											{msg.media && (
												<div className="mb-2 overflow-hidden rounded-lg bg-muted/60 border">
													{msg.media.type === "image" ? (
														<img
															src={msg.media.url || "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=400&q=80"}
															alt="Mídia"
															className="max-h-56 w-full object-cover"
															onError={(e) => {
																e.target.src = "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=400&q=80";
															}}
														/>
													) : msg.media.type === "audio" ? (
														<div className="flex items-center gap-3 p-3">
															<div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground">
																<Mic className="h-4 w-4" />
															</div>
															<div className="flex-1">
																<div className="h-2 w-full rounded-full bg-primary/20">
																	<div className="h-full w-1/3 rounded-full bg-primary" />
																</div>
																<span className="text-[10px] text-muted-foreground">Áudio simulado</span>
															</div>
														</div>
													) : (
														<div className="flex items-center gap-2 p-3 text-xs">
															<FileText className="h-5 w-5 text-primary" />
															<span className="truncate">{msg.media.url || "Documento anexado"}</span>
														</div>
													)}
													{msg.media.caption && (
														<p className="p-2 text-xs border-t bg-background/50 whitespace-pre-wrap">{msg.media.caption}</p>
													)}
												</div>
											)}

											{msg.text && <p className="whitespace-pre-wrap leading-relaxed">{msg.text}</p>}

											{/* Interactive Options for Menu / Buttons / List / AI */}
											{msg.options && msg.options.length > 0 && (
												<div className="mt-2.5 space-y-1.5 pt-1.5 border-t border-border/60">
													{msg.options.map((opt, i) => (
														<Button
															key={i}
															type="button"
															variant="outline"
															size="sm"
															onClick={() => handleOptionSelect(opt)}
															disabled={isTyping || !waitingNode || waitingNode.id !== msg.nodeId}
															className="w-full justify-start text-xs font-medium h-auto py-2 px-3 text-left border-primary/25 hover:border-primary hover:bg-primary/5 active:scale-[0.99] transition-all"
														>
															<span className="mr-2 flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary font-bold text-[11px]">
																{opt.number || i + 1}
															</span>
															<div className="truncate">
																<p className="font-semibold leading-tight">{opt.label || `Opção ${opt.number}`}</p>
																{opt.description && (
																	<p className="text-[10px] text-muted-foreground truncate">{opt.description}</p>
																)}
															</div>
														</Button>
													))}
												</div>
											)}

											{msg.footer && <p className="mt-1 text-[11px] text-muted-foreground">{msg.footer}</p>}

											<div className="mt-1 flex items-center justify-end text-[10px] text-muted-foreground/80">
												<span>{msg.timestamp}</span>
											</div>
										</div>
									</div>
								);
							})}

							{/* Typing indicator */}
							{isTyping && (
								<div className="flex justify-start animate-in fade-in duration-100">
									<div className="flex items-center gap-1.5 rounded-2xl rounded-tl-xs bg-card border px-3.5 py-2.5 text-xs text-muted-foreground shadow-sm">
										<span className="flex gap-1">
											<span className="h-1.5 w-1.5 rounded-full bg-primary animate-bounce [animation-delay:-0.3s]" />
											<span className="h-1.5 w-1.5 rounded-full bg-primary animate-bounce [animation-delay:-0.15s]" />
											<span className="h-1.5 w-1.5 rounded-full bg-primary animate-bounce" />
										</span>
										<span className="text-[11px] font-medium">{typingText}</span>
									</div>
								</div>
							)}

							<div ref={chatEndRef} />
						</div>

						{/* Bottom input area */}
						<div className="border-t bg-card/90 backdrop-blur p-3 relative z-10">
							<form onSubmit={handleSendMessage} className="flex items-center gap-2">
								<Input
									ref={inputRef}
									value={userInput}
									onChange={(e) => setUserInput(e.target.value)}
									placeholder={
										waitingNode?.type === "ai"
											? "Converse com o assistente virtual..."
											: waitingNode?.type === "question"
											? "Digite sua resposta..."
											: waitingNode?.type === "menu" || waitingNode?.type === "buttons"
											? "Clique em uma opção acima ou digite o número..."
											: isFinished
											? "Fluxo concluído (clique em reiniciar para testar de novo)"
											: "Digite uma mensagem para simular o cliente..."
									}
									disabled={isTyping}
									className="h-11 flex-1 bg-background text-sm rounded-xl"
									autoFocus
								/>
								<Button
									type="submit"
									disabled={isTyping || !userInput.trim()}
									size="icon"
									className="h-11 w-11 shrink-0 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white shadow-md"
								>
									<Send className="h-4 w-4" />
								</Button>
							</form>
						</div>
					</div>

					{/* Variables Inspector Side Panel */}
					{showVariables && (
						<div className="w-80 border-l bg-card p-4 overflow-y-auto flex flex-col shrink-0 animate-in slide-in-from-right-4 duration-200">
							<div className="flex items-center justify-between pb-3 border-b mb-3">
								<div className="flex items-center gap-2 font-semibold text-sm">
									<Braces className="h-4 w-4 text-primary" />
									<span>Variáveis Coletadas</span>
								</div>
								<Badge variant="secondary" className="text-[11px] font-mono">
									{Object.keys(context).length}
								</Badge>
							</div>

							<p className="text-xs text-muted-foreground mb-3">
								Valores armazenados em memória durante a execução do fluxo:
							</p>

							<div className="space-y-2 flex-1 overflow-y-auto">
								{Object.entries(context).map(([key, val]) => (
									<div key={key} className="rounded-lg border bg-muted/40 p-2.5 text-xs font-mono">
										<div className="flex items-center justify-between text-muted-foreground mb-1">
											<span className="font-semibold text-primary">{`{{${key}}}`}</span>
											<span className="text-[10px] text-muted-foreground/70">{typeof val}</span>
										</div>
										<div className="text-foreground break-all bg-background/60 p-1.5 rounded border text-[11px]">
											{typeof val === "object" ? JSON.stringify(val) : String(val)}
										</div>
									</div>
								))}
							</div>

							<div className="pt-3 border-t mt-3 flex justify-between items-center text-xs text-muted-foreground">
								<span>100% em memória</span>
								<Button
									variant="outline"
									size="sm"
									onClick={() => setContext({})}
									className="h-7 text-xs px-2"
								>
									Limpar variáveis
								</Button>
							</div>
						</div>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default FlowSimulator;
